import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../hooks/useToast';
import { apiSend } from '../lib/api';
import { Lightbulb } from 'lucide-react';
import { AppTile } from '../components/AppTile';
import { RequestAccessModal } from '../components/RequestAccessModal';
import { SuggestAppModal } from '../components/SuggestAppModal';
import { Skeleton } from '../components/Skeleton';
import { cn } from '../lib/cn';
import type { AppRecord, MeResponse } from '../lib/types';

const useDebounced = (value: string, delay = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

export const AllApps = () => {
  const { getToken, previewUserId } = useAuth();
  const { toast } = useToast();
  const me = useApi<MeResponse>('/api/me');
  const apps = useApi<AppRecord[]>('/api/apps');

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('All');
  const [scope, setScope] = useState<'all' | 'internal' | 'external'>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [requestApp, setRequestApp] = useState<AppRecord | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const dq = useDebounced(query);

  const readOnly = previewUserId !== null;

  const categories = useMemo(() => {
    if (!apps.data) return ['All'];
    const set = new Set<string>(apps.data.map((a) => a.category));
    return ['All', ...Array.from(set).sort()];
  }, [apps.data]);

  const filtered = useMemo(() => {
    if (!apps.data) return [];
    const q = dq.trim().toLowerCase();
    return apps.data.filter((a) => {
      const catOk = category === 'All' || a.category === category;
      if (!catOk) return false;
      const scopeOk =
        scope === 'all' ||
        (scope === 'internal' && a.isInternal) ||
        (scope === 'external' && !a.isInternal);
      if (!scopeOk) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        (a.description ?? '').toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
      );
    });
  }, [apps.data, dq, category, scope]);

  const pinnedSet = useMemo(
    () => new Set(me.data?.pins.map((p) => p.appId) ?? []),
    [me.data],
  );

  const togglePin = useCallback(
    async (app: AppRecord) => {
      const wasPinned = pinnedSet.has(app.id);
      setBusyId(app.id);

      me.mutate((prev) => {
        if (!prev) return prev;
        if (wasPinned) {
          return { ...prev, pins: prev.pins.filter((p) => p.appId !== app.id) };
        }
        return {
          ...prev,
          pins: [...prev.pins, { appId: app.id, order: prev.pins.length }],
        };
      });

      try {
        const token = await getToken();
        if (wasPinned) {
          await apiSend('DELETE', `/api/pins/${app.id}`, undefined, token);
        } else {
          await apiSend('POST', '/api/pins', { appId: app.id }, token);
        }
        me.refetch();
      } catch (err) {
        me.mutate((prev) => {
          if (!prev) return prev;
          if (wasPinned) {
            return {
              ...prev,
              pins: [...prev.pins, { appId: app.id, order: prev.pins.length }],
            };
          }
          return { ...prev, pins: prev.pins.filter((p) => p.appId !== app.id) };
        });
        toast({
          kind: 'error',
          message: `Couldn't ${wasPinned ? 'unpin' : 'pin'} ${app.name}. Try again.`,
        });
        // eslint-disable-next-line no-console
        console.error('pin toggle failed', err);
      } finally {
        setBusyId(null);
      }
    },
    [pinnedSet, me, getToken, toast],
  );

  return (
    <div className="space-y-6">
      <RequestAccessModal app={requestApp} onClose={() => setRequestApp(null)} />
      <SuggestAppModal open={suggestOpen} onClose={() => setSuggestOpen(false)} />
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">All apps</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Every app in the catalogue. Click a locked tile to request access.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSuggestOpen(true)}
          disabled={readOnly}
          title={readOnly ? 'Disabled while previewing as another user' : undefined}
          className="inline-flex h-10 items-center gap-1.5 rounded-md border border-line bg-bg-primary px-3 text-sm font-medium text-text-secondary hover:bg-bg-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-bg-primary disabled:hover:text-text-secondary"
        >
          <Lightbulb className="h-4 w-4" />
          Suggest an app
        </button>
      </header>

      <div className="space-y-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, description, category..."
            className="h-10 w-full rounded-md border border-line bg-bg-primary px-3 text-sm outline-none ring-accent focus:border-accent focus:ring-2 md:max-w-md"
            aria-label="Filter apps"
          />
          {/* In-house vs external scope */}
          <div
            role="radiogroup"
            aria-label="Source"
            className="inline-flex h-10 shrink-0 overflow-hidden rounded-md border border-line"
          >
            {([
              { id: 'all', label: 'All' },
              { id: 'internal', label: 'In-house' },
              { id: 'external', label: 'External' },
            ] as const).map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={scope === opt.id}
                onClick={() => setScope(opt.id)}
                className={cn(
                  'px-3 text-xs font-medium transition-colors',
                  scope === opt.id
                    ? 'bg-accent text-accent-contrast'
                    : 'bg-bg-primary text-text-secondary hover:bg-bg-secondary',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                category === cat
                  ? 'border-accent bg-accent text-accent-contrast'
                  : 'border-line bg-bg-primary text-text-secondary hover:border-line-strong',
              )}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {apps.loading ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line p-8 text-center">
          <p className="text-sm text-text-secondary">No apps match.</p>
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setCategory('All');
              setScope('all');
            }}
            className="mt-2 text-xs text-accent hover:underline"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((app) => (
            <AppTile
              key={app.id}
              app={app}
              pinned={pinnedSet.has(app.id)}
              onTogglePin={togglePin}
              onRequestAccess={setRequestApp}
              busy={busyId === app.id}
              preview={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
};
